<div class="min-h-full bg-slate-50 p-6">
  <div class="mx-auto max-w-6xl rounded-2xl border border-slate-200 bg-white shadow-sm">
    <!-- Header -->
    <div class="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 px-6 py-5">
      <div>
        <h1 class="text-xl font-semibold text-slate-900">{{ 'tariff.title' | translate }}</h1>
        <p class="mt-1 text-sm text-slate-500">{{ 'tariff.subtitle' | translate }}</p>
      </div>
      <button
        type="button"
        (click)="openCreate()"
        class="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-colors">
        <mat-icon class="!h-5 !w-5 !text-[20px]">add</mat-icon>
        {{ 'tariff.new' | translate }}
      </button>
    </div>

    <!-- Search -->
    <div class="px-6 pt-4">
      <div class="relative max-w-sm">
        <mat-icon class="pointer-events-none absolute left-3 top-1/2 !h-5 !w-5 !text-[20px] -translate-y-1/2 text-slate-400">search</mat-icon>
        <input
          type="text"
          [ngModel]="searchName()"
          (ngModelChange)="onSearchChange($event)"
          [placeholder]="'tariff.searchPlaceholder' | translate"
          class="w-full rounded-lg border border-slate-300 bg-white py-2 pl-10 pr-9 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
        @if (searchName()) {
          <button
            type="button"
            (click)="clearSearch()"
            [attr.aria-label]="'tariff.clearSearch' | translate"
            class="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <mat-icon class="!h-4 !w-4 !text-[16px]">close</mat-icon>
          </button>
        }
      </div>
    </div>

    <!-- Status filter -->
    <div class="flex items-center gap-1 px-6 pt-3">
      <button
        type="button"
        (click)="setFilter(null)"
        class="rounded-lg px-3 py-1.5 text-sm font-medium transition-colors"
        [class.bg-blue-600]="statusFilter() === null"
        [class.text-white]="statusFilter() === null"
        [class.text-slate-500]="statusFilter() !== null"
        [class.hover:bg-slate-100]="statusFilter() !== null">
        {{ 'tariff.all' | translate }}
      </button>
      @for (s of statuses; track s) {
        <button
          type="button"
          (click)="setFilter(s)"
          class="rounded-lg px-3 py-1.5 text-sm font-medium transition-colors"
          [class.bg-blue-600]="statusFilter() === s"
          [class.text-white]="statusFilter() === s"
          [class.text-slate-500]="statusFilter() !== s"
          [class.hover:bg-slate-100]="statusFilter() !== s">
          {{ ('tariff.status.' + s) | translate }}
        </button>
      }
    </div>

    <!-- Loading -->
    @if (loading()) {
      <div class="flex items-center justify-center py-20">
        <div class="h-9 w-9 animate-spin rounded-full border-[3px] border-slate-200 border-t-blue-600"></div>
      </div>
    }

    <!-- Error -->
    @if (!loading() && errorMessage()) {
      <div class="m-6 flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-4 py-3">
        <div class="flex items-center gap-2 text-sm text-red-700">
          <mat-icon class="!h-5 !w-5 !text-[20px]">error_outline</mat-icon>
          {{ errorMessage() }}
        </div>
        <button type="button" (click)="load()" class="text-sm font-semibold text-red-700 hover:text-red-900">{{ 'tariff.retry' | translate }}</button>
      </div>
    }

    <!-- Empty: no results for the current search/filter -->
    @if (!loading() && !errorMessage() && tariffs().length === 0 && searchName()) {
      <div class="flex flex-col items-center justify-center px-6 py-20 text-center">
        <div class="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
          <mat-icon class="!h-7 !w-7 !text-[28px]">search_off</mat-icon>
        </div>
        <h3 class="mt-4 text-sm font-semibold text-slate-900">{{ 'tariff.noResults.title' | translate }}</h3>
        <p class="mt-1 max-w-sm text-sm text-slate-500">{{ 'tariff.noResults.body' | translate: { term: searchName() } }}</p>
        <button
          type="button"
          (click)="clearSearch()"
          class="mt-5 inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">
          {{ 'tariff.clearSearch' | translate }}
        </button>
      </div>
    }

    <!-- Empty: no tariffs at all -->
    @if (!loading() && !errorMessage() && tariffs().length === 0 && !searchName()) {
      <div class="flex flex-col items-center justify-center px-6 py-20 text-center">
        <div class="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
          <mat-icon class="!h-7 !w-7 !text-[28px]">sell</mat-icon>
        </div>
        <h3 class="mt-4 text-sm font-semibold text-slate-900">{{ 'tariff.empty.title' | translate }}</h3>
        <p class="mt-1 max-w-sm text-sm text-slate-500">{{ 'tariff.empty.body' | translate }}</p>
        <button
          type="button"
          (click)="openCreate()"
          class="mt-5 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition-colors">
          <mat-icon class="!h-5 !w-5 !text-[20px]">add</mat-icon>
          {{ 'tariff.new' | translate }}
        </button>
      </div>
    }

    <!-- Table -->
    @if (!loading() && !errorMessage() && tariffs().length > 0) {
      <div class="overflow-x-auto px-2 pb-2">
        <table class="min-w-full">
          <thead>
            <tr class="text-left text-xs font-medium uppercase tracking-wider text-slate-400">
              <th class="px-4 py-3 font-medium">{{ 'tariff.col.name' | translate }}</th>
              <th class="px-4 py-3 font-medium">{{ 'tariff.col.pricing' | translate }}</th>
              <th class="px-4 py-3 font-medium">{{ 'tariff.col.priority' | translate }}</th>
              <th class="px-4 py-3 font-medium">{{ 'tariff.col.rules' | translate }}</th>
              <th class="px-4 py-3 font-medium">{{ 'tariff.col.versions' | translate }}</th>
              <th class="px-4 py-3 font-medium">{{ 'tariff.col.status' | translate }}</th>
              <th class="px-4 py-3 text-right font-medium">{{ 'tariff.col.actions' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            @for (t of tariffs(); track t.id) {
              <tr class="border-t border-slate-100 hover:bg-slate-50/70">
                <td class="px-4 py-3.5">
                  <button type="button" (click)="toggleExpand(t.id)" class="flex items-center gap-2 text-left">
                    <mat-icon class="!h-5 !w-5 !text-[20px] text-slate-400 transition-transform" [class.rotate-90]="expandedId() === t.id">chevron_right</mat-icon>
                    <span class="min-w-0">
                      <span class="block text-sm font-medium text-slate-900">{{ t.name }}</span>
                      @if (t.description) {
                        <span class="block max-w-xs truncate text-xs text-slate-400">{{ t.description }}</span>
                      }
                    </span>
                  </button>
                </td>
                <td class="px-4 py-3.5 text-sm font-medium text-slate-800">{{ priceSummary(activeVersion(t)) }}</td>
                <td class="px-4 py-3.5 text-sm text-slate-600">{{ t.priority }}</td>
                <td class="px-4 py-3.5 text-sm text-slate-600">{{ t.rules?.length ?? 0 }}</td>
                <td class="px-4 py-3.5 text-sm text-slate-600">{{ t.versions?.length ?? 0 }}</td>
                <td class="px-4 py-3.5">
                  <span class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset" [class]="statusClass(t.status)">
                    {{ ('tariff.status.' + (t.status ?? '')) | translate }}
                  </span>
                </td>
                <td class="px-4 py-3.5">
                  <div class="flex items-center justify-end gap-1">
                    <button type="button" (click)="openEdit(t)" [disabled]="busyId() === t.id" [attr.aria-label]="'tariff.edit' | translate"
                      class="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-blue-600 disabled:opacity-40">
                      <mat-icon class="!h-5 !w-5 !text-[20px]">edit</mat-icon>
                    </button>
                    @if (t.status === 'Draft') {
                      <button type="button" (click)="activate(t)" [disabled]="busyId() === t.id" [attr.aria-label]="'tariff.activate' | translate"
                        class="rounded-lg p-1.5 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-40">
                        <mat-icon class="!h-5 !w-5 !text-[20px]">play_circle</mat-icon>
                      </button>
                    }
                    @if (t.status !== 'Archived') {
                      <button type="button" (click)="archive(t)" [disabled]="busyId() === t.id" [attr.aria-label]="'tariff.archive' | translate"
                        class="rounded-lg p-1.5 text-slate-400 hover:bg-amber-50 hover:text-amber-600 disabled:opacity-40">
                        <mat-icon class="!h-5 !w-5 !text-[20px]">inventory_2</mat-icon>
                      </button>
                    }
                    <button type="button" (click)="remove(t)" [disabled]="busyId() === t.id" [attr.aria-label]="'tariff.delete' | translate"
                      class="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40">
                      <mat-icon class="!h-5 !w-5 !text-[20px]">delete_outline</mat-icon>
                    </button>
                  </div>
                </td>
              </tr>

              <!-- Expanded: versions/pricing + rules -->
              @if (expandedId() === t.id) {
                <tr class="border-t border-slate-100 bg-slate-50/60">
                  <td colspan="7" class="px-6 py-5">
                    <div class="grid gap-6 lg:grid-cols-2">

                      <!-- Versions & pricing -->
                      <section>
                        <div class="flex items-center justify-between">
                          <h4 class="text-xs font-semibold uppercase tracking-wider text-slate-400">{{ 'tariff.versionsTitle' | translate }}</h4>
                          <button type="button" (click)="addVersion(t)"
                            class="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:border-blue-300 hover:text-blue-600 transition-colors">
                            <mat-icon class="!h-4 !w-4 !text-[16px]">add</mat-icon>
                            {{ 'tariff.version.addVersion' | translate }}
                          </button>
                        </div>
                        @if ((t.versions?.length ?? 0) === 0) {
                          <p class="mt-3 text-sm text-slate-400">{{ 'tariff.noVersions' | translate }}</p>
                        } @else {
                          <ul class="mt-3 space-y-3">
                            @for (v of t.versions ?? []; track v.id) {
                              <li class="rounded-xl border border-slate-200 bg-white p-4">
                                <div class="flex items-center justify-between gap-3">
                                  <div class="flex items-center gap-2">
                                    <span class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset" [class]="versionStatusClass(v.status)">{{ ('tariff.status.' + (v.status ?? '')) | translate }}</span>
                                    <span class="text-xs text-slate-400">{{ 'tariff.rev' | translate }} {{ v.revisionNumber }}</span>
                                  </div>
                                  <div class="flex items-center gap-2">
                                    <span class="text-sm font-semibold text-slate-900">{{ priceSummary(v) }}</span>
                                    @if (v.status === 'Draft') {
                                      <button type="button" (click)="activateVersion(t, v)" [disabled]="busyId() === v.id" [attr.aria-label]="'tariff.version.activate' | translate"
                                        class="rounded-lg p-1 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-40">
                                        <mat-icon class="!h-4 !w-4 !text-[16px]">play_circle</mat-icon>
                                      </button>
                                    }
                                    @if (v.status === 'Active') {
                                      <button type="button" (click)="supersedeVersion(t, v)" [disabled]="busyId() === v.id" [attr.aria-label]="'tariff.version.supersede' | translate"
                                        class="rounded-lg p-1 text-slate-400 hover:bg-amber-50 hover:text-amber-600 disabled:opacity-40">
                                        <mat-icon class="!h-4 !w-4 !text-[16px]">history</mat-icon>
                                      </button>
                                    }
                                  </div>
                                </div>
                                <div class="mt-2 text-xs text-slate-500">
                                  {{ 'tariff.valid' | translate }} {{ shortDate(v.validFrom) }} → {{ v.validTo ? shortDate(v.validTo) : ('tariff.open' | translate) }}
                                  @if (v.pricingPolicy) { <span class="text-slate-400">· {{ v.pricingPolicy.timeBasis }}</span> }
                                  @if (v.pricingPolicy && v.pricingPolicy.allowedEntries !== 1) { <span class="text-slate-400">· {{ entriesLabel(v.pricingPolicy.allowedEntries) }}</span> }
                                </div>

                                @if (v.pricingPolicy?.rateBands?.length) {
                                  <div class="mt-3">
                                    <p class="text-xs font-medium text-slate-400">{{ 'tariff.rateBands' | translate }} <span class="text-slate-300">({{ v.pricingPolicy?.timeZoneId }})</span></p>
                                    <ul class="mt-1 space-y-0.5 text-sm text-slate-600">
                                      @for (band of v.pricingPolicy?.rateBands ?? []; track band.id) {
                                        <li class="flex items-center justify-between">
                                          <span>{{ bandTime(band.startTime) }}–{{ bandTime(band.endTime) }} → <span class="font-medium text-slate-800">{{ money(band.amount, band.currency) }}</span></span>
                                          <button type="button" (click)="deleteRateBand(t, v, band)" [attr.aria-label]="'tariff.rule.conditions.remove' | translate"
                                            class="rounded p-0.5 text-slate-300 hover:text-red-600">
                                            <mat-icon class="!h-3.5 !w-3.5 !text-[14px]">close</mat-icon>
                                          </button>
                                        </li>
                                      }
                                    </ul>
                                  </div>
                                }
                                @if (v.pricingPolicy?.rateTiers?.length) {
                                  <div class="mt-3">
                                    <p class="text-xs font-medium text-slate-400">{{ 'tariff.rateTiers' | translate }}</p>
                                    <ul class="mt-1 space-y-0.5 text-sm text-slate-600">
                                      @for (tier of v.pricingPolicy?.rateTiers ?? []; track tier.id) {
                                        <li class="flex items-center justify-between">
                                          <span>{{ 'tariff.after' | translate }} {{ minutes(tier.afterMinutes) }} → <span class="font-medium text-slate-800">{{ money(tier.newAmount, tier.newCurrency) }}</span></span>
                                          <button type="button" (click)="deleteRateTier(t, v, tier)" [attr.aria-label]="'tariff.rule.conditions.remove' | translate"
                                            class="rounded p-0.5 text-slate-300 hover:text-red-600">
                                            <mat-icon class="!h-3.5 !w-3.5 !text-[14px]">close</mat-icon>
                                          </button>
                                        </li>
                                      }
                                    </ul>
                                  </div>
                                }
                                @if (v.pricingPolicy?.rateCaps?.length) {
                                  <div class="mt-3">
                                    <p class="text-xs font-medium text-slate-400">{{ 'tariff.caps' | translate }}</p>
                                    <ul class="mt-1 space-y-0.5 text-sm text-slate-600">
                                      @for (cap of v.pricingPolicy?.rateCaps ?? []; track cap.id) {
                                        <li class="flex items-center justify-between">
                                          <span>{{ cap.window }} → {{ 'tariff.max' | translate }} <span class="font-medium text-slate-800">{{ money(cap.maxAmount, cap.maxCurrency) }}</span> <span class="text-slate-400">({{ cap.windowAnchor }})</span></span>
                                          <button type="button" (click)="deleteRateCap(t, v, cap)" [attr.aria-label]="'tariff.rule.conditions.remove' | translate"
                                            class="rounded p-0.5 text-slate-300 hover:text-red-600">
                                            <mat-icon class="!h-3.5 !w-3.5 !text-[14px]">close</mat-icon>
                                          </button>
                                        </li>
                                      }
                                    </ul>
                                  </div>
                                }
                                @if (v.pricingPolicy?.gracePeriods?.length) {
                                  <div class="mt-3">
                                    <p class="text-xs font-medium text-slate-400">{{ 'tariff.grace' | translate }}</p>
                                    <ul class="mt-1 space-y-0.5">
                                      @for (g of v.pricingPolicy?.gracePeriods ?? []; track g.id) {
                                        <li class="flex items-center justify-between text-sm text-slate-600">
                                          <span>{{ g.trigger }} · {{ minutes(g.durationMinutes) }} · {{ g.behavior }}</span>
                                          <button type="button" (click)="deleteGracePeriod(t, v, g)" [attr.aria-label]="'tariff.rule.conditions.remove' | translate"
                                            class="rounded p-0.5 text-slate-300 hover:text-red-600">
                                            <mat-icon class="!h-3.5 !w-3.5 !text-[14px]">close</mat-icon>
                                          </button>
                                        </li>
                                      }
                                    </ul>
                                  </div>
                                }
                              </li>
                            }
                          </ul>
                        }
                      </section>

                      <!-- Eligibility rules -->
                      <section>
                        <div class="flex items-center justify-between">
                          <div>
                            <h4 class="text-xs font-semibold uppercase tracking-wider text-slate-400">
                              {{ 'tariff.rulesTitle' | translate }}
                              <span class="ml-1.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-600">{{ t.rules?.length ?? 0 }}</span>
                            </h4>
                            <p class="mt-0.5 text-xs text-slate-400">{{ 'tariff.rulesHint' | translate }}</p>
                          </div>
                          <button type="button" (click)="addRule(t)"
                            class="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:border-blue-300 hover:text-blue-600 transition-colors">
                            <mat-icon class="!h-4 !w-4 !text-[16px]">add</mat-icon>
                            {{ 'tariff.addRule' | translate }}
                          </button>
                        </div>
                        @if ((t.rules?.length ?? 0) === 0) {
                          <p class="mt-3 text-sm text-slate-400">{{ 'tariff.noRules' | translate }}</p>
                        } @else {
                          <ul class="mt-3 space-y-2">
                            @for (rule of t.rules ?? []; track rule.id) {
                              <li class="group rounded-xl border border-slate-200 bg-white transition-colors hover:border-blue-300 hover:bg-blue-50/20">
                                <!-- Effect first: an Exclude rule that reads like an Include one is the
                                     difference between billing a stay and not billing it. -->
                                <div class="flex items-stretch">
                                  <div class="w-1 shrink-0 rounded-l-xl"
                                    [class.bg-emerald-400]="rule.effect === 'Include'"
                                    [class.bg-red-400]="rule.effect === 'Exclude'"></div>

                                  <div class="flex min-w-0 flex-1 items-start justify-between gap-3 px-4 py-3">
                                    <div class="min-w-0">
                                      <div class="flex flex-wrap items-center gap-1.5">
                                        <span class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset"
                                          [class.bg-emerald-50]="rule.effect === 'Include'" [class.text-emerald-700]="rule.effect === 'Include'" [class.ring-emerald-600/20]="rule.effect === 'Include'"
                                          [class.bg-red-50]="rule.effect === 'Exclude'" [class.text-red-700]="rule.effect === 'Exclude'" [class.ring-red-600/20]="rule.effect === 'Exclude'">
                                          {{ rule.effect }}
                                        </span>
                                        <span class="text-xs text-slate-400">{{ 'tariff.rule.matchesLabel' | translate }}</span>
                                        @for (f of ruleFields(rule); track f.labelKey) {
                                          <span class="inline-flex items-center rounded-full px-2 py-0.5 text-xs ring-1 ring-inset" [class]="chipClass(f.tone)">
                                            <span class="opacity-60">{{ ('tariff.field.' + f.labelKey) | translate }}:</span>&nbsp;<span class="font-medium">{{ f.value }}</span>
                                          </span>
                                        }
                                        @if (ruleFields(rule).length === 0) {
                                          <span class="text-sm font-medium text-amber-700">{{ 'tariff.anyParker' | translate }}</span>
                                        }
                                      </div>

                                      @if (rule.conditions?.length) {
                                        <ul class="mt-2 flex flex-wrap gap-1.5">
                                          @for (c of rule.conditions ?? []; track c.id) {
                                            <li class="inline-flex items-center rounded-md bg-slate-50 px-2 py-0.5 font-mono text-xs text-slate-600 ring-1 ring-inset ring-slate-200">
                                              {{ c.negate ? 'NOT ' : '' }}{{ c.kind }} {{ c.operator }} {{ c.value }}{{ c.valueTo ? ('-' + c.valueTo) : '' }}
                                            </li>
                                          }
                                        </ul>
                                      }

                                      <p class="mt-2 text-xs text-slate-400">
                                        {{ 'tariff.priorityWord' | translate }} {{ rule.priority }} &middot; {{ 'tariff.specificity' | translate }} {{ rule.specificityCache }}
                                      </p>
                                    </div>

                                    <div class="flex shrink-0 items-center gap-1">
                                      <button type="button" (click)="editRule(t, rule)" [attr.aria-label]="'tariff.editRule' | translate"
                                        [title]="'tariff.editRule' | translate"
                                        class="rounded-lg p-1.5 text-slate-400 hover:bg-blue-50 hover:text-blue-600">
                                        <mat-icon class="!h-5 !w-5 !text-[20px]">edit</mat-icon>
                                      </button>
                                      <button type="button" (click)="removeRule(t, rule)" [attr.aria-label]="'tariff.deleteRule' | translate"
                                        [title]="'tariff.deleteRule' | translate"
                                        class="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600">
                                        <mat-icon class="!h-5 !w-5 !text-[20px]">delete_outline</mat-icon>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </li>
                            }
                          </ul>
                        }
                      </section>
                    </div>
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      @if (totalPages() > 1) {
        <div class="flex items-center justify-between border-t border-slate-100 px-6 py-3">
          <p class="text-sm text-slate-500">
            {{ 'tariff.page' | translate }} {{ pageIndex() }} {{ 'tariff.ofWord' | translate }} {{ totalPages() }} · {{ totalCount() }} {{ 'tariff.totalWord' | translate }}
          </p>
          <div class="flex gap-2">
            <button type="button" (click)="goToPage(pageIndex() - 1)" [disabled]="pageIndex() === 1"
              class="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40">
              {{ 'tariff.prev' | translate }}
            </button>
            <button type="button" (click)="goToPage(pageIndex() + 1)" [disabled]="pageIndex() >= totalPages()"
              class="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40">
              {{ 'tariff.next' | translate }}
            </button>
          </div>
        </div>
      }
    }
  </div>
</div>
